import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { LogoMark } from "../components/logo";
import { ApiError, membersApi } from "../lib/api";
import { listsQuery } from "../lib/queries";
import { requireSession } from "../lib/session";

export const Route = createFileRoute("/invitations/list")({
  validateSearch: (search: Record<string, unknown>): { token: string } => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  // The invitee signs in (or creates an account) first, then comes back to this URL with
  // the token intact, so accepting always happens as the right person.
  beforeLoad: ({ context, location }) => requireSession(context.queryClient, location.href),
  component: AcceptInvitationPage,
});

function AcceptInvitationPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const accept = useMutation({
    mutationFn: () => membersApi.accept(token),
    onSuccess: ({ listId }) => {
      void queryClient.invalidateQueries({ queryKey: listsQuery.queryKey });
      void navigate({ to: "/mes-listes/$listId", params: { listId }, replace: true });
    },
  });

  useEffect(() => {
    if (token) accept.mutate();
    // Accept once on arrival; a re-render must not send the token again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const invalid =
    !token || (accept.error instanceof ApiError && [400, 404, 410].includes(accept.error.status));

  return (
    <main className="mx-auto grid max-w-md justify-items-start gap-4 px-4 py-24">
      <LogoMark className="size-12" />
      {accept.isError || !token ? (
        <>
          <h1 className="text-4xl font-semibold">
            {invalid
              ? "Cette invitation n'est plus valable"
              : "L'invitation n'a pas pu être acceptée"}
          </h1>
          <p className="text-lg text-muted">
            {invalid
              ? "Elle a peut-être expiré ou déjà servi. Demandez à la personne qui vous a invité de vous en renvoyer une."
              : accept.error instanceof ApiError
                ? accept.error.message
                : "Réessayez dans un instant."}
          </p>
          <Link to="/mes-listes" className="btn">
            Aller à mes listes
          </Link>
        </>
      ) : (
        <h1 className="text-3xl font-semibold">On vous ouvre la liste…</h1>
      )}
    </main>
  );
}
